import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Box,
    Button,
    FormLabel,
    Switch,
    Text,
    useColorModeValue,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { ChatBackend } from "models/organization/dto/organization.dto";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { useElevatedAdminAccess } from "../hooks/useIsElevatedAdmin";
import { useSelectedOrganization } from "../hooks/useSelectedOrganization";
import { useUpdateOrganizationChatBackend } from "../hooks/useUpdateOrganizationChatBackend";

// Phase 6 chat cutover flag — elevated (platform) admins ONLY; the API 403s
// regular operators, and this card never renders for them. Flipping swaps the
// tenant's whole mobile chat between GetStream and the in-house backend on the
// users' next flags refresh (≤1h). Message history does not carry across
// backends, so the switch is guarded by an explicit confirmation dialog.
// Prerequisites (backfill + parity checks) live in
// docs/runbooks/chat-cutover.md — read it before flipping a real tenant.
export default function ChatBackendSettingsCard() {
    const { isElevated: isElevatedAdmin } = useElevatedAdminAccess();
    const { selectedOrganization } = useSelectedOrganization();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement>(null);
    // The backend the pending confirmation would switch to.
    const [pendingBackend, setPendingBackend] = useState<ChatBackend | null>(null);

    // Missing field (older API) = the server-side default.
    const currentBackend = selectedOrganization?.chatBackend ?? ChatBackend.STREAM;
    const isInHouse = currentBackend === ChatBackend.INHOUSE;

    const { mutate, isLoading } = useUpdateOrganizationChatBackend(
        selectedOrganization?.id || '',
        () => {
            toast({
                title: translate('organization.chat_backend_saved'),
                status: 'success',
                duration: 2000,
                position: 'top-right',
            });
        },
    );

    if (!isElevatedAdmin || !selectedOrganization) return null;

    const requestToggle = (nextInHouse: boolean) => {
        if (isLoading) return; // guard against double-flips while in flight
        setPendingBackend(nextInHouse ? ChatBackend.INHOUSE : ChatBackend.STREAM);
        onOpen();
    };

    const confirmToggle = () => {
        onClose();
        if (!pendingBackend) return;
        mutate(
            { chatBackend: pendingBackend },
            {
                onError: () => {
                    toast({
                        title: translate('error'),
                        status: 'error',
                        duration: 3000,
                        position: 'top-right',
                    });
                },
            },
        );
    };

    return (
        <PageCard title={translate('organization.chat_backend_title')}>
            <Box p={4}>
                <Box display="flex" alignItems="center" gap={3} mb={2}>
                    <Switch
                        isChecked={isInHouse}
                        isDisabled={isLoading}
                        onChange={(e) => requestToggle(e.target.checked)}
                        colorScheme="brand"
                        aria-label={translate('organization.chat_backend_inhouse_label')}
                    />
                    <FormLabel mb={0} color={textColorPrimary}>
                        {translate('organization.chat_backend_inhouse_label')}
                    </FormLabel>
                </Box>
                <Text fontSize="sm" color="gray.500">
                    {translate('organization.chat_backend_helper')}
                </Text>
            </Box>

            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {translate('organization.chat_backend_confirm_title')}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {translate('organization.chat_backend_confirm_body')}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClose}>
                                {translate('organization.chat_backend_confirm_cancel')}
                            </Button>
                            <Button colorScheme="red" onClick={confirmToggle} ml={3}>
                                {translate('organization.chat_backend_confirm_action')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </PageCard>
    );
}
